import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, Check, Gift, Coins, ArrowLeft, CalendarDays } from "lucide-react";
import { API_BASE } from "../config";
import "../styles/calendarPage.css";
import apiFetch from "../apiFetch";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const STREAK_MESSAGES = [
  { min: 30, message: "Legendary! A full month of mastery!", emoji: "🏆" },
  { min: 15, message: "Incredible! Two weeks of dedication!", emoji: "⭐" },
  { min: 9,  message: "You're on fire! Keep blazing!", emoji: "🔥" },
  { min: 5,  message: "You're on a roll! Keep it up!", emoji: "💪" },
  { min: 2,  message: "Nice! You're building momentum!", emoji: "🚀" },
  { min: 1,  message: "Welcome back! Great to see you!", emoji: "👋" },
];

function getStreakMessage(streak) {
  for (const entry of STREAK_MESSAGES) {
    if (streak >= entry.min) return entry;
  }
  return { message: "Start your streak today!", emoji: "🌱" };
}

function buildCalendarGrid(year, month) {
  // month is 1-based
  const firstDay = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const userId = parseInt(localStorage.getItem("userId"), 10);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState(null);

  const fetchCalendar = useCallback(() => {
    if (!userId) return;
    apiFetch(`/api/calendar/${userId}`)
      .then((r) => r.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [userId]);

  // Check in on page load
  useEffect(() => {
    if (!userId) return;
    apiFetch(`/api/calendar/checkin`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    })
      .then((r) => r.json())
      .then(() => fetchCalendar())
      .catch(() => fetchCalendar());
  }, [userId, fetchCalendar]);

  const claimBonus = () => {
    if (claiming || !data?.bonusAvailable) return;
    setClaiming(true);
    apiFetch(`/api/calendar/claim-bonus`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    })
      .then((r) => r.json())
      .then((res) => {
        setClaimResult(res.coinsAwarded);
        setClaiming(false);
        fetchCalendar();
      })
      .catch(() => setClaiming(false));
  };

  if (loading) {
    return (
      <div className="cal-loading">
        <CalendarDays size={40} className="cal-spin" />
        <p>Loading your calendar…</p>
      </div>
    );
  }

  const { year, month, today, loggedDays = [], streak = 0, bonusAvailable, bonusAmount } = data || {};
  const cells = buildCalendarGrid(year, month);
  const loggedSet = new Set(loggedDays);
  const streakMsg = getStreakMessage(streak);
  const daysInMonth = new Date(year, month, 0).getDate();

  return (
    <div className="cal-page">
      {/* Header */}
      <div className="cal-header">
        <button className="cal-back-btn" onClick={() => navigate("/home")}>
          <ArrowLeft size={16} /> Back
        </button>
        <div className="cal-header-center">
          <CalendarDays size={22} />
          <h1>Login Calendar</h1>
        </div>
        <div className="cal-streak-badge">
          <Flame size={16} />
          <span>{streak} day streak</span>
        </div>
      </div>

      <div className="cal-body">
        {/* Month title */}
        <div className="cal-month-title">
          {MONTH_NAMES[(month || 1) - 1]} {year}
        </div>

        {/* Calendar grid */}
        <div className="cal-grid-wrap">
          <div className="cal-day-labels">
            {DAY_LABELS.map((d) => <span key={d}>{d}</span>)}
          </div>
          <div className="cal-grid">
            {cells.map((day, i) => {
              if (!day) return <div key={`empty-${i}`} className="cal-cell cal-empty" />;
              const logged = loggedSet.has(day);
              const isToday = day === today;
              const isFuture = day > today;
              return (
                <div
                  key={day}
                  className={`cal-cell ${logged ? "logged" : ""} ${isToday ? "today" : ""} ${isFuture ? "future" : ""}`}
                >
                  <span className="cal-day-num">{day}</span>
                  {logged && (
                    <span className="cal-tick">
                      <Check size={13} strokeWidth={3} />
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Progress bar */}
        <div className="cal-progress-wrap">
          <div className="cal-progress-bar">
            <div
              className="cal-progress-fill"
              style={{ width: `${((loggedDays.length / daysInMonth) * 100).toFixed(1)}%` }}
            />
          </div>
          <span className="cal-progress-label">{loggedDays.length}/{daysInMonth} days this month</span>
        </div>

        {/* Claim bonus */}
        <div className="cal-bonus-section">
          {claimResult != null ? (
            <div className="cal-claimed-banner">
              <Coins size={20} />
              <span>+{claimResult} coins collected! Come back tomorrow for more.</span>
            </div>
          ) : bonusAvailable ? (
            <button className="cal-claim-btn" onClick={claimBonus} disabled={claiming}>
              <Gift size={18} />
              {claiming ? "Collecting…" : `Collect Login Bonus  +${bonusAmount} coins`}
            </button>
          ) : (
            <div className="cal-bonus-collected">
              <Check size={16} /> Login bonus already collected today
            </div>
          )}
        </div>

        {/* Streak bar */}
        <div className="cal-streak-section">
          <div className="cal-streak-flames">
            {Array.from({ length: Math.min(streak, 31) }).map((_, i) => (
              <Flame
                key={i}
                size={20}
                className={`cal-flame ${i < streak ? "active" : ""}`}
                style={{ animationDelay: `${i * 0.05}s` }}
              />
            ))}
          </div>
          <div className="cal-streak-message">
            <span className="cal-streak-emoji">{streakMsg.emoji}</span>
            <span className="cal-streak-text">{streakMsg.message}</span>
            {streak > 0 && (
              <span className="cal-streak-count">{streak} day{streak !== 1 ? "s" : ""}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
